/**
 * The J-3704 data source: fake data behind the real `VaultDataSource` seam.
 *
 * J-3705 replaces this with the read-only replica (03 Option A) without touching any screen.
 * Variants exist so a reviewer can see every list state (01-references §6 S3) without editing
 * code. Each one is selected with a `?fixture=` URL parameter; see `fixtureVariantFromSearch`.
 *
 * Like any data source it loads only for a live unlock session (CSO review of PR #95), even
 * though its data is fake, so the shell's session handling is exercised on fixture builds too.
 * Fixture builds only: the bundle-safety test fails if a non-fixture build contains it.
 */
import type { Clock, UnlockSession, VaultDataSource, VaultSnapshot } from "../contract.js";
import { systemClock } from "../contract.js";
import { isLiveSession } from "../auth/session.js";
import { FIXTURE_WALLETS, fixtureItems } from "./fixtures.js";

export type FixtureVariant = "normal" | "stale" | "failed-wallet" | "empty";

export const FIXTURE_VARIANTS: readonly FixtureVariant[] = ["normal", "stale", "failed-wallet", "empty"];

const HOUR = 3_600_000;

export class FixtureDataSource implements VaultDataSource {
  readonly kind = "fixture" as const;

  constructor(
    private readonly variant: FixtureVariant = "normal",
    private readonly clock: Clock = systemClock,
  ) {}

  async load(session: UnlockSession): Promise<VaultSnapshot> {
    if (!isLiveSession(session)) throw new Error("Locked: this copy opens only after an unlock.");
    const now = this.clock.now();
    // A normal replica is a couple of hours old; a stale one is three days old.
    const ageMs = this.variant === "stale" ? 72 * HOUR : 2 * HOUR;
    let items = fixtureItems();
    const failedWallets = [];
    if (this.variant === "failed-wallet") {
      // Partial index (Quick Search rule 11): "work" failed, "home" and "shared" still list.
      items = items.filter((i) => i.wallet !== "work");
      failedWallets.push({ wallet: "work", reason: "Could not open this wallet's copy (FAKE failure for review)" });
    }
    if (this.variant === "empty") items = [];
    return {
      wallets: FIXTURE_WALLETS.map((w) => ({ ...w })),
      items,
      asOf: new Date(now - ageMs).toISOString(),
      failedWallets,
      fixture: true,
    };
  }
}

/** Reads `?fixture=<variant>`. Anything unrecognised falls back to `normal`. */
export function fixtureVariantFromSearch(search: string): FixtureVariant {
  const v = new URLSearchParams(search).get("fixture");
  return (FIXTURE_VARIANTS as readonly string[]).includes(v ?? "") ? (v as FixtureVariant) : "normal";
}
